import React, { useState, useEffect } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";

import { AppConfig } from "../../constants";
import { InfoCard } from "../../pages/public/SignUp/components/InfoCard";
import { TaskSquareIcon, DollarCircleIcon } from "../../pages/public/SignUp/components/LeftSideWrapper/styles";

import Image03 from "../../assets/img/Image03.png";
import { AuthLeftPanelContainer, AuthLeftPanelOverlay, AuthLeftPanelContent } from "./styles";

const LOGO = AppConfig.appLogo2;

export interface AuthLeftPanelProps {
  showContentDelay?: number;
}

const AuthLeftPanel: React.FC<AuthLeftPanelProps> = ({ showContentDelay = 300 }) => {
  const { t: translate } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);

  useEffect(() => {
    const image = new Image();
    const timeouts: NodeJS.Timeout[] = [];

    image.src = Image03;
    image.onload = () => setIsLoaded(true);
    image.onerror = () => setIsLoaded(false);

    // Depois anima o conteúdo
    const stages = [
      { delay: showContentDelay + 200, stage: 1 },
      { delay: showContentDelay + 400, stage: 2 },
      { delay: showContentDelay + 600, stage: 3 },
      { delay: showContentDelay + 800, stage: 4 },
    ];

    stages.forEach(({ delay, stage }) => {
      const timeoutId = setTimeout(() => setAnimationStage(stage), delay);
      timeouts.push(timeoutId);
    });

    return () => {
      timeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      image.onload = null;
      image.onerror = null;
    };
  }, [showContentDelay]);

  const getAnimationStyle = (stage: number) => ({
    opacity: animationStage >= stage ? 1 : 0,
    transform: animationStage >= stage ? "translateY(0)" : "translateY(20px)",
    transition: "all 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
  });

  return (
    <AuthLeftPanelContainer>
      <Box
        component="img"
        src={Image03}
        alt="Shipeezi background"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1,
          opacity: isLoaded ? 1 : 0,
          transition: "opacity 0.5s ease-in-out",
        }}
        loading="eager"
        decoding="async"
      />

      <AuthLeftPanelOverlay />

      <AuthLeftPanelContent>
        <Stack
          direction="column"
          height="100%"
          sx={{
            position: "relative",
            zIndex: 3,
            color: "#fff",
            px: { xs: 4, lg: 6 },
            py: { xs: 5, lg: 7 },
            justifyContent: "space-between",
          }}
        >
          <Stack spacing={{ xs: 4, lg: 5 }} sx={{ pt: { xs: 8, lg: 10 } }}>
            <Box sx={getAnimationStyle(1)}>
              <Box
                component="img"
                src={LOGO}
                alt="Shipeezi"
                sx={{
                  width: "160px",
                  height: "auto",
                }}
              />
            </Box>
            <Box sx={getAnimationStyle(2)}>
              <Typography
                fontSize={{ xs: "28px", lg: "32px" }}
                lineHeight={{ xs: "36px", lg: "40px" }}
                fontWeight={700}
                fontFamily="Manrope"
                sx={{ letterSpacing: "-0.02em" }}
              >
                {translate("signup.left.title") || "One platform, every move"}
              </Typography>
            </Box>
          </Stack>

          <Stack spacing={{ xs: 2.5, lg: 3 }} sx={{ pb: { xs: 4, lg: 6 } }}>
            <Box sx={getAnimationStyle(3)}>
              <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 2.5, lg: 3 }}>
                <InfoCard
                  icon={<TaskSquareIcon size="21.5" color="#fff" />}
                  title={translate("signup.left.card1Title") || "Simplicity at scale"}
                  info={translate("signup.left.card1Desc") || "Shipeezi brings all your processes together in one place, helping your team work smarter and faster across the globe."}
                />
                <InfoCard
                  icon={<DollarCircleIcon size="21.5" color="#fff" />}
                  title={translate("signup.left.card2Title") || "More control, less cost"}
                  info={translate("signup.left.card2Desc") || "With everything connected, you'll save time, reduce errors, and cut costs – while giving your customers a seamless experience."}
                />
              </Stack>
            </Box>
          </Stack>
        </Stack>
      </AuthLeftPanelContent>
    </AuthLeftPanelContainer>
  );
};

export default AuthLeftPanel;
